<template>
  <WdsStack class="tw-justify-between" gap-sm align-center>
    <WdsStack gap-md>
      <WdsButton
        tertiary
        :disabled="filtersDisabled && !selectedClientOwnerIds.length"
        :class="{ 'filters-active': filtersAreOpened }"
        @click="filtersAreOpened = !filtersAreOpened"
      >
        <div class="tw-my-auto">
          <WdsStack gap-sm align-center>
            <WdsIcon name="settings-adjust" :size="16" />
            <WdsText>
              {{ $t('Filter') }}
            </WdsText>
          </WdsStack>
        </div>
      </WdsButton>
      <WdsStack v-if="filtersAreOpened" gap-md>
        <WdsSelect
          v-model="billWorkerStatus.data"
          :list="$options.statusOptions"
          :placeholder="$t('Status')"
          class="tw-w-[200px]"
          can-deselect
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-model="selectedClients.data"
          multi-select
          can-deselect
          searchable
          :list="clients"
          :placeholder="$t('Clients')"
          class="tw-w-[200px]"
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-if="clientOwnerOptions.length || selectedClientOwnerIds.length"
          v-model="selectedClientOwners"
          multi-select
          can-deselect
          searchable
          :list="clientOwnerOptions"
          :placeholder="$t('Client owner(s)')"
          class="tw-w-[200px]"
          data-test="payment-requests-client-owners-filter"
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-model="paymentRequestJobTitles.data"
          multi-select
          can-deselect
          searchable
          :list="jobTitles"
          :placeholder="$t('Hire Titles')"
          class="tw-w-[200px]"
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
      </WdsStack>
    </WdsStack>
    <WdsStack v-if="!viewerIsStaffingAgency">
      <a
        href="https://help.worksome.dk/en/articles/10026605-when-will-i-get-paid"
        target="_blank"
        class="tw-ml-auto"
      >
        {{ $t('When will I get paid?') }}
      </a>
    </WdsStack>
  </WdsStack>
</template>

<script>
import { WdsIcon, WdsText, WdsStack, WdsSelect, WdsButton } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, inject } from 'vue'
import { $t } from '@/composables/translation'
import useDefaultClientOwnerFilter from '@/composables/useDefaultClientOwnerFilter'
import useQueryEncoder from '@/composables/useQueryEncoder'
import { user } from '@/composables/useUser'
import { LocalStorageEncoder } from '@/utils/Encoders'
import {
  PaymentRequestWorkerStatus,
  SupplierClientOwnersFilterOptionsDocument,
} from '@/gql/graphql'
import { atobSafe, btoaSafe } from '@/components/GraphqlDataTable/filters/filter-tools'
import { getWorkerStatusLabel } from '@/components/PaymentRequest/utilities/statusFormatting'

const decodeClientOwners = (raw) => {
  try {
    const ids = raw !== '' ? JSON.parse(atobSafe(raw)) : null
    return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : null
  } catch {
    return null
  }
}

const encodeClientOwners = (ids) => (ids != null ? btoaSafe(JSON.stringify(ids)) : '')

const ownerLabel = (owner) =>
  owner.id === user.value.globalId ? $t('Me (:user)', { user: owner.label }) : owner.label

export default {
  setup() {
    const viewerIsStaffingAgency = inject('viewerIsStaffingAgency')

    const { loading: defaultClientOwnerLoading, clientOwners: defaultClientOwners } =
      useDefaultClientOwnerFilter()

    // Absent from the URL decodes to undefined (use the default); an explicit [] stays cleared.
    const urlClientOwners = useQueryEncoder(
      'clientOwners',
      null,
      decodeClientOwners,
      encodeClientOwners,
    )

    const selectedClientOwnerIds = computed(
      () => urlClientOwners.value ?? defaultClientOwners.value,
    )

    const { result: clientOwnersResult } = useQuery(
      SupplierClientOwnersFilterOptionsDocument,
      {},
      { clientId: 'publicApi', enabled: Boolean(viewerIsStaffingAgency) },
    )

    const clientOwnerOptions = computed(() => {
      const owners = (clientOwnersResult.value?.companyClients?.data ?? []).flatMap((client) =>
        (client?.owners ?? []).map((owner) => owner.user),
      )

      return Array.from(new Map(owners.map((owner) => [owner.id, owner])).values())
        .map((owner) => ({ ...owner, label: ownerLabel(owner) }))
        .sort((a, b) => (a.id === user.value.globalId ? -1 : b.id === user.value.globalId ? 1 : 0))
    })

    const selectedClientOwners = computed({
      get: () =>
        selectedClientOwnerIds.value.map(
          (id) =>
            clientOwnerOptions.value.find((owner) => owner.value === id) ?? {
              id,
              value: id,
              label: ownerLabel({ id, label: id === user.value.globalId ? user.value.name : id }),
            },
        ),
      set: (owners) => {
        urlClientOwners.value = (owners ?? []).map((owner) => owner.value)
      },
    })

    return {
      viewerIsStaffingAgency,
      defaultClientOwnerLoading,
      selectedClientOwnerIds,
      clientOwnerOptions,
      selectedClientOwners,
    }
  },
  statusOptions: [
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Draft),
      value: PaymentRequestWorkerStatus.Draft,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Requested),
      value: PaymentRequestWorkerStatus.Requested,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Approved),
      value: PaymentRequestWorkerStatus.Approved,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Processing),
      value: PaymentRequestWorkerStatus.Processing,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Completed),
      value: PaymentRequestWorkerStatus.Completed,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Rejected),
      value: PaymentRequestWorkerStatus.Rejected,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Cancelled),
      value: PaymentRequestWorkerStatus.Cancelled,
    },
  ],
  components: {
    WdsButton,
    WdsSelect,
    WdsText,
    WdsIcon,
    WdsStack,
  },
  props: {
    filtersDisabled: {
      type: Boolean,
      default: false,
    },
    clients: {
      type: Array,
      default: () => [],
    },
    jobTitles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filtersAreOpened: false,
      selectedClients: new LocalStorageEncoder({
        storageKey: 'clients',
        decoder: this.decodeClientOptionsFromUrl,
        filter: (statuses) => statuses.map((status) => status.value),
        defaultVal: [],
      }),
      paymentRequestJobTitles: new LocalStorageEncoder({
        storageKey: 'job-titles',
        decoder: this.decodeJobTitlesOptionsFromUrl,
        filter: (statuses) => statuses.map((status) => status.value),
        defaultVal: [],
      }),
      billWorkerStatus: new LocalStorageEncoder({
        storageKey: 'worker-status',
        decoder: this.decodeStatusOptionsFromUrl,
        filter: (statuses) => statuses.value,
        defaultVal: null,
      }),
    }
  },
  computed: {
    filter() {
      if (this.defaultClientOwnerLoading) {
        return null
      }

      return {
        workerStatus: this.billWorkerStatus.data?.value,
        jobTitles: (this.paymentRequestJobTitles.data ?? []).map((status) => status.value),
        companies: (this.selectedClients.data ?? []).map((status) => status.value),
        ...(this.selectedClientOwnerIds.length
          ? { clientOwners: this.selectedClientOwnerIds }
          : {}),
      }
    },
  },
  watch: {
    filter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.$emit('filters-updated', newVal)
        }
      },
    },
    defaultClientOwnerLoading: {
      immediate: true,
      handler(loading) {
        // Show an applied owner filter so it can be seen and cleared.
        if (!loading && this.selectedClientOwnerIds.length) {
          this.filtersAreOpened = true
        }
      },
    },
  },
  methods: {
    $t,
    decodeStatusOptionsFromUrl(data) {
      return this.$options.statusOptions?.filter((option) => data === option.value)
    },
    decodeClientOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.clients?.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    decodeJobTitlesOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.jobTitles?.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
  },
}
</script>
